// Version history for a parallel-grid tile (POR-14659).
//
// A tile keeps every image it produced in `versions` and the one on screen in
// `versionIndex`. Its top-level `url` / `overlayUrl` / `_mrhiCallouts` and
// `context.productMode` always mirror that version, so the thumbnail strip, the
// download, the lightbox and Create Layout keep reading the tile as before and
// stepping back to v1 really undoes everywhere. Tiles saved before versions
// existed have none: their current image counts as the only version.
//
// Pure (no imports) so it can be verified with `node --test`.

// Overlay text is part of the version too: an Edit can bake text in or change the
// editable text, and stepping back must give Create Layout the text of THAT version.
function versionOf(source) {
  return {
    url: source.url || null,
    overlayUrl: source.overlayUrl || null,
    _mrhiCallouts: source._mrhiCallouts || null,
    productMode: source.productMode || null,
    overlayText: source.overlayText || null,
    overlayTextMode: source.overlayTextMode || 'editable',
    libraryUrl: source.libraryUrl || null,
  };
}

export function tileVersions(tile) {
  if (tile && Array.isArray(tile.versions) && tile.versions.length) {
    return tile.versions;
  }

  if (!tile || !tile.url) {
    return [];
  }

  return [versionOf({
    url: tile.url,
    overlayUrl: tile.overlayUrl,
    _mrhiCallouts: tile._mrhiCallouts,
    productMode: tile.context && tile.context.productMode,
    overlayText: tile.overlayText,
    overlayTextMode: tile.overlayTextMode,
  })];
}

export function tileVersionCount(tile) {
  return tileVersions(tile).length;
}

export function currentTileVersionIndex(tile) {
  const count = tileVersionCount(tile);

  if (!count) {
    return -1;
  }

  const index = Number.isInteger(tile.versionIndex) ? tile.versionIndex : count - 1;

  return Math.min(Math.max(index, 0), count - 1);
}

function mirror(tile, versions, index) {
  const version = versions[index];
  // Versions recorded before overlay text was versioned don't carry it: keep the tile's.
  const hasText = Object.prototype.hasOwnProperty.call(version, 'overlayText');

  return {
    ...tile,
    versions,
    versionIndex: index,
    url: version.url,
    overlayUrl: version.overlayUrl || null,
    _mrhiCallouts: version._mrhiCallouts || null,
    overlayText: hasText ? (version.overlayText || null) : (tile.overlayText || null),
    overlayTextMode: hasText ? (version.overlayTextMode || 'editable') : (tile.overlayTextMode || 'editable'),
    context: tile.context ? { ...tile.context, productMode: version.productMode || null } : tile.context,
  };
}

export function appendTileVersion(tile, version) {
  const versions = [...tileVersions(tile), versionOf(version)];

  return mirror(tile, versions, versions.length - 1);
}

export function selectTileVersion(tile, index) {
  const versions = tileVersions(tile);

  if (!versions.length) {
    return tile;
  }

  return mirror(tile, versions, Math.min(Math.max(Number(index) || 0, 0), versions.length - 1));
}

export function patchTileVersion(tile, index, patch) {
  const versions = tileVersions(tile).slice();

  if (index < 0 || index >= versions.length) {
    return tile;
  }

  versions[index] = { ...versions[index], ...patch };

  return index === currentTileVersionIndex(tile)
    ? mirror(tile, versions, index)
    : { ...tile, versions };
}

// The one shape every "a tile finished an image" path produces: `done`, the
// result as a new current version, and the generation context (overridable, e.g.
// Regenerate pins the Layered/Static mode the user picked).
export function finishedTileFromResult(tile, result, { context, tilePatch } = {}) {
  const nextContext = context !== undefined ? context : (result && result.context) || null;
  const attachment = (result && result.attachment) || {};
  // Pin the existing history BEFORE the context changes: a tile saved before versions
  // existed synthesizes its v1 from its own context, not from the new result's.
  const prior = { ...tile, versions: tileVersions(tile) };
  const patched = { ...prior, ...(tilePatch || {}) };

  return appendTileVersion(
    { ...patched, state: 'done', context: nextContext },
    {
      url: result.imageUrl,
      overlayUrl: attachment.overlayUrl || null,
      _mrhiCallouts: attachment._mrhiCallouts || null,
      productMode: (nextContext && nextContext.productMode) || null,
      overlayText: patched.overlayText,
      overlayTextMode: patched.overlayTextMode,
    },
  );
}
